/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useLocation, useParams } from "react-router";
import { Link } from "../components/ui/Link";
import {ChevronDown, Check, History, Lock, LockOpen, MailCheck, Pencil, Send, TriangleAlert, UserCog } from "lucide-react";
import {
  accountChanges,
  accountDetails,
  editAccount,
  moveToFamily,
  reinviteCoParent,
  resendCareTeamInvite,
  retryHealthieSync,
  sendPasswordReset,
  sendVerification,
  setAccountRoles,
  setCanReceiveMessages,
  setAccountStatus,
  unlockAccount,
  ALLOWED_STATUS_TRANSITIONS,
  CLIENT_ROLES,
  INTERNAL_ROLES,
  type AccountChange,
  type AccountDetails,
  type EditAccountBody,
  type HealthieRefusal,
  type HealthieSyncField,
} from "../api/accounts";
import { useAuth } from "../auth/AuthContext";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { CopyableId } from "../components/ui/CopyableId";
import { CopyIconButton } from "../components/ui/CopyIconButton";
import { HealthieLink } from "../components/HealthieLink";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { StaffCoverage } from "../components/StaffCoverage";
import { Input } from "../components/ui/Input";
import { DropdownPanel } from "../components/ui/DropdownPanel";
import { Modal } from "../components/ui/Modal";
import { Select } from "../components/ui/Select";
import { Spinner } from "../components/ui/Spinner";
import { GuardedButton } from "../components/ui/GuardedButton";
import { useChrome } from "../contexts/ChromeContext";
import { fromOf } from "../lib/navigation";
import { Facts, Section } from "../components/DetailBits";
import { useToast } from "../contexts/ToastContext";
import { cn } from "../lib/utils";
import {
  formatWhen,
  fullName,
  isCareStaff,
  isStaffAccount,
  neverRegistered,
  statusLabel,
  statusTone,
  roleLabel,
} from "../lib/accounts";
import { ValidationError } from "../lib/validation";
import { BusyLabel } from "../components/ui/BusyLabel";

const STAFF_TARGET_HINT = "Only admins can change staff accounts.";
const CONCURRENT_EDIT = "concurrently";
const HEALTHIE_REFUSAL_TITLE = "Healthie didn't accept this change";
const HEALTHIE_NO_REASON = "Healthie didn't give a reason";
const HEALTHIE_FIELD_LABELS: Record<HealthieSyncField, string> = {
  email: "Email",
  phoneE164: "Phone",
  firstName: "First name",
  lastName: "Last name",
  legalFirstName: "Legal first name",
};
const CHANGE_FIELD_LABELS: Record<string, string> = {
  careTeam: "Care team",
  careTeamCoverage: "Care team coverage",
  canReceiveMessages: "Can receive family messages",
};

function HealthieRecord({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-3">
      <HealthieLink
        healthieId={value}
        className="text-sm font-medium text-accent hover:underline"
        ariaLabel={`${label} ${value}`}
      >
        {label} <span className="font-mono">{value}</span>
      </HealthieLink>
      <CopyIconButton label={label} value={value} />
    </div>
  );
}

/**
 * Back to the list the viewer came from, else to the list this kind of account lives in, once it
 * has loaded: Care team for a care-team member, Access for other staff, Families for a client.
 */
function backLink(
  fromPath: string | undefined,
  details: AccountDetails | null,
): { to: string; label: string } | undefined {
  const to = fromPath ?? (details === null ? undefined : homeList(details));
  return to === undefined ? undefined : { to, label: listLabel(to) };
}

function homeList(details: AccountDetails): string {
  if (details.careTeamKind !== null) {
    return "/care/team";
  }
  return isStaffAccount(details.roles) ? "/admin/access" : "/care/families";
}

function listLabel(path: string): string {
  if (path.startsWith("/admin/access")) {
    return "Access";
  }
  if (path.startsWith("/care/team")) {
    return "Care team";
  }
  return path.startsWith("/care/families/") ? "Family" : "Families";
}

export function AccountDetail() {
  const { id } = useParams();
  const location = useLocation();
  const { hasRole, session } = useAuth();
  const { showToast } = useToast();

  const [details, setDetails] = useState<AccountDetails | null>(null);
  const [changes, setChanges] = useState<AccountChange[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editingRoles, setEditingRoles] = useState(false);
  const [movingFamily, setMovingFamily] = useState(false);
  const [targetFamily, setTargetFamily] = useState("");
  const [moveNote, setMoveNote] = useState("");
  const [confirming, setConfirming] = useState<
    "reset" | "verification" | "status" | "messaging" | null
  >(null);
  const [statusTarget, setStatusTarget] = useState("");
  const [messagingTarget, setMessagingTarget] = useState(false);
  const [busy, setBusy] = useState(false);


  const requestSeq = useRef(0);
  const reload = useCallback(async () => {
    if (id === undefined) {
      return;
    }
    const seq = ++requestSeq.current;
    const [loaded, history] = await Promise.all([accountDetails(id), accountChanges(id)]);
    if (seq !== requestSeq.current) {
      return;
    }
    if (loaded.ok) {
      setDetails(loaded.value);
      setLoadError(null);
    } else {
      setLoadError(loaded.error.message);
    }
    setChanges(history.ok ? history.value.items : []);
  }, [id]);

  useEffect(() => {
    setDetails(null);
    setChanges(null);
    setLoadError(null);
    void reload();
  }, [reload]);

  const name = details
    ? fullName(details.profile.firstName, details.profile.lastName)
    : "Account";
  const back = backLink(fromOf(location.state)?.pathname, details);
  useChrome(
    {
      title: name,
      ...(back === undefined ? {} : { back }),
      sidePanel:
        changes !== null ? (
          <aside className="space-y-3">
            <h2 className="text-sm font-medium uppercase tracking-wide text-text-tertiary">
              Recent changes
            </h2>
            <div className="border border-border bg-bg-elevated shadow-card rounded-3xl p-5">
              <ChangeList changes={changes} />
            </div>
          </aside>
        ) : undefined,
    },
    [name, back?.to, back?.label, changes],
  );

  if (id === undefined || (details === null && loadError === null)) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (details === null) {
    return <ErrorMessage message={loadError} className="max-w-3xl" />;
  }

  const viewerIsAdmin = hasRole("Admin");
  const viewerIsCareStaff = isCareStaff(hasRole);
  const staffTargetLocked = !viewerIsAdmin && isStaffAccount(details.roles);
  const transitions = ALLOWED_STATUS_TRANSITIONS[details.status] ?? [];
  const isSelf =
    session.state === "authenticated" && session.user.id === String(details.profile.id);
  const isClientAccount = details.roles.some((role) =>
    (CLIENT_ROLES as readonly string[]).includes(role),
  );
  const canEditRoles = viewerIsAdmin && !isSelf && !isClientAccount;

  async function runAction<T>(
    action: () => Promise<{ ok: true; value: T } | { ok: false; error: { message: string } }>,
    successMessage: (value: T) => string,
  ) {
    setBusy(true);
    setBanner(null);
    const result = await action();
    setBusy(false);
    if (result.ok) {
      showToast("success", successMessage(result.value));
      void reload();
    } else {
      setBanner(result.error.message);
    }
  }

  return (
    <div className="max-w-5xl space-y-6 pb-4">
      <header className="enter flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
            {name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {neverRegistered(details.status, details.profile.username) ? (
              <Badge tone="warning">Never registered</Badge>
            ) : (
              <Badge tone={statusTone(details.status)}>{statusLabel(details.status)}</Badge>
            )}
            {details.lockout.lockedOut && (
              <Badge tone="error">
                <Lock size={10} className="mr-1 inline-block" />
                Locked out
              </Badge>
            )}
            <span className="text-xs text-text-tertiary">
              {details.roles.map(roleLabel).join(" · ")}
            </span>
            <CopyableId label="Account" value={String(details.profile.id)} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GuardedButton
            locked={staffTargetLocked}
            hint={STAFF_TARGET_HINT}
            variant="secondary"
            size="md"
            onClick={() => {
              setBanner(null);
              setEditing(true);
            }}
          >
            <Pencil size={15} />
            Edit
          </GuardedButton>
          <EmailActionsMenu
            locked={staffTargetLocked}
            hint={STAFF_TARGET_HINT}
            emailOnFile={details.profile.email !== null}
            onPick={(action) => setConfirming(action)}
          />
          {details.lockout.lockedOut && (
            <GuardedButton
              locked={staffTargetLocked}
              hint={STAFF_TARGET_HINT}
              variant="secondary"
              size="md"
              disabled={busy}
              onClick={() =>
                void runAction(
                  () => unlockAccount(id),
                  () => "Account unlocked.",
                )
              }
            >
              <LockOpen size={15} />
              Unlock
            </GuardedButton>
          )}
          {canEditRoles && (
            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                setBanner(null);
                setEditingRoles(true);
              }}
            >
              <UserCog size={15} />
              Roles
            </Button>
          )}
          {viewerIsAdmin && transitions.length > 0 && (
            <Select
              className="w-44"
              value=""
              placeholder="Change status"
              options={transitions.map((target) => ({
                value: target,
                label: `To ${statusLabel(target)}`,
              }))}
              onChange={(target) => {
                setStatusTarget(target);
                setConfirming("status");
              }}
            />
          )}
        </div>
      </header>

      {banner !== null && (
        <div role="alert" className="enter">
          <ErrorMessage
            message={banner}
            description={
              banner.includes(CONCURRENT_EDIT)
                ? "This view has been refreshed with the latest state."
                : null
            }
            className="mb-0"
          />
        </div>
      )}

      {details.healthieSync?.fields.length ? (
        <HealthieRefusals
          refusals={details.healthieSync.fields}
          locked={staffTargetLocked}
          disabled={busy}
          onRetry={() =>
            runAction(
              () => retryHealthieSync(id),
              () => "Sent to Healthie again",
            )
          }
        />
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Section title="Profile">
          <Facts
            facts={[
              [
                "Email",
                <span key="email" className="inline-flex flex-wrap items-center gap-1.5">
                  <span className="break-all">{details.profile.email ?? "—"}</span>
                  {details.profile.email === null ? null : details.emailVerification.verified ? (
                    <Badge tone="success">
                      <MailCheck size={10} className="mr-1 inline-block" />
                      Verified
                    </Badge>
                  ) : (
                    <Badge tone="warning">Unverified</Badge>
                  )}
                </span>,
              ],
              ["Phone", details.profile.phone ?? "—"],
              [
                "Legal name",
                details.profile.legalFirstName
                  ? [details.profile.legalFirstName, details.profile.lastName]
                      .filter((part) => part !== null)
                      .join(" ")
                  : "—",
              ],
              ["Username", details.profile.username ?? "—"],
              ["Time zone", details.profile.timeZone ?? "—"],
              ["Last active", formatWhen(details.profile.lastActiveAt)],
              ["Created", formatWhen(details.audit.createdAt)],
            ]}
          />
        </Section>

        <Section title="Security">
          <Facts
            facts={[
              ["Lifecycle status", statusLabel(details.status)],
              [
                "Lockout",
                details.lockout.lockedOut
                  ? `Locked out after ${details.lockout.failedLoginCount} failed logins`
                  : `Not locked out (${details.lockout.failedLoginCount} recent failures)`,
              ],
              ["Last failed login", formatWhen(details.lockout.lastFailedLoginAt)],
              [
                "Active sessions",
                <span key="sessions">
                  {details.sessions.total}
                  <span className="ml-1.5 text-xs text-text-tertiary">
                    ({details.sessions.refreshTokens} refresh ·{" "}
                    {details.sessions.legacySessionTokens} legacy)
                  </span>
                </span>,
              ],
              ["Email verified at", formatWhen(details.emailVerification.verifiedAt)],
            ]}
          />
        </Section>

        {isClientAccount && (
        <Section title="Family">
          {details.profile.familyId === null ? (
            <p className="text-sm text-text-tertiary">Not part of a family.</p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 border-b border-border/60 pb-3">
                <Link
                  to={`/care/families/${details.profile.familyId}`}
                  className="text-sm font-medium text-interactive hover:underline"
                >
                  Family
                </Link>
                <span className="flex items-center gap-2">
                  <CopyableId label="Family" value={String(details.profile.familyId)} />
                  {viewerIsCareStaff && isClientAccount && (
                    <Button variant="ghost" size="sm" onClick={() => setMovingFamily(true)}>
                      Move to family
                    </Button>
                  )}
                </span>
              </div>
              <ul className="space-y-2.5">
                {details.familyMembers.map((member) => (
                  <li
                    key={member.id}
                    className="flex items-center justify-between gap-3 text-sm"
                  >
                    <Link
                      to={`/accounts/${member.id}`}
                      className="min-w-0 truncate font-medium text-text-primary hover:text-interactive"
                    >
                      {fullName(member.firstName, member.lastName, `Account ${member.id}`)}
                    </Link>
                    <span className="flex shrink-0 items-center gap-1.5">
                      <span className="text-xs text-text-tertiary">
                        {member.roles.map(roleLabel).join(" · ")}
                      </span>
                      <Badge tone={statusTone(member.status)}>
                        {statusLabel(member.status)}
                      </Badge>
                      {member.lockedOut && <Badge tone="error">Locked out</Badge>}
                    </span>
                  </li>
                ))}
                {details.familyMembers.length === 0 && (
                  <li className="text-sm text-text-tertiary">No other members.</li>
                )}
              </ul>
            </div>
          )}
        </Section>
        )}

        <Section title="Healthie links">
          {details.patients.length === 0 && details.staff.length === 0 ? (
            <p className="text-sm text-text-tertiary">No linked Healthie records.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {details.patients.map((link) => (
                <HealthieRecord key={link.id} label="Client" value={link.externalId ?? link.id} />
              ))}
              {details.staff.map((link) => (
                <HealthieRecord key={link.id} label="Staff" value={link.externalId ?? link.id} />
              ))}
            </div>
          )}
        </Section>

        <Section title="Invites">
          <InviteList
            details={details}
            busy={busy}
            onResendCareTeam={(inviteId) =>
              void runAction(
                () => resendCareTeamInvite(inviteId),
                () => "Invite email re-sent.",
              )
            }
            onReinviteCoParent={(email, programKey) =>
              void runAction(
                () => reinviteCoParent(email, programKey),
                () => "Co-parent invite email re-sent.",
              )
            }
          />
        </Section>

        {viewerIsCareStaff && details.careTeam && (
          <Section title="Family messaging">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Facts
                facts={[["Can receive family messages", messagingLabel(details.canReceiveMessages)]]}
              />
              <span className="flex gap-2">
                {messagingTargets(details.canReceiveMessages).map((target) => (
                  <Button
                    key={String(target)}
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      setMessagingTarget(target);
                      setConfirming("messaging");
                    }}
                  >
                    {target ? "Turn on" : "Turn off"}
                  </Button>
                ))}
              </span>
            </div>
          </Section>
        )}

        <Section title="Recent changes" className="xl:hidden">
          <ChangeList changes={changes} />
        </Section>
      </div>

      {viewerIsCareStaff && details.careTeamKind !== null && (
        <StaffCoverage
          staffId={id}
          name={name}
          coordinator={details.careTeamKind === "CARE_COORDINATOR"}
        />
      )}

      <EditAccountModal
        isOpen={editing}
        details={details}
        onClose={() => setEditing(false)}
        onSaved={(message) => {
          setEditing(false);
          showToast("success", message);
          void reload();
        }}
        onConcurrentConflict={(message) => {
          setEditing(false);
          setBanner(message);
          void reload();
        }}
      />

      <RolesModal
        isOpen={editingRoles}
        accountId={id}
        current={details.roles}
        onClose={() => setEditingRoles(false)}
        onSaved={(roles) => {
          setEditingRoles(false);
          showToast(
            "success",
            roles.length === 0 ? "All roles revoked." : `Roles set to ${roles.map(roleLabel).join(", ")}.`,
          );
          void reload();
        }}
      />

      <Modal
        isOpen={movingFamily}
        onClose={() => setMovingFamily(false)}
        title="Move to another family"
        size="sm"
      >
        <div className="space-y-4 px-6 py-5">
          <p className="text-sm leading-relaxed text-text-secondary">
            The account joins the target family and gains its caregiver relationships. If nothing
            else lives in the current family, the empty family is deleted. The change is logged and
            synced.
          </p>
          <Input
            id="move-target-family"
            label="Target family id"
            inputMode="numeric"
            value={targetFamily}
            onChange={(event) => setTargetFamily(event.target.value)}
            placeholder="e.g. 15756"
          />
          <Input
            id="move-family-note"
            label="Reason"
            value={moveNote}
            onChange={(event) => setMoveNote(event.target.value)}
            placeholder="Why this account belongs in that family"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setMovingFamily(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={busy || !/^\d+$/.test(targetFamily.trim()) || moveNote.trim() === ""}
              onClick={() => {
                setMovingFamily(false);
                void runAction(
                  () => moveToFamily(id, Number(targetFamily.trim()), moveNote.trim()),
                  (value) => `Moved to family ${value.familyId}.`,
                );
              }}
            >
              Move account
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={confirming === "reset"}
        onClose={() => setConfirming(null)}
        title="Send password reset"
        size="sm"
      >
        <div className="space-y-4 px-6 py-5">
          <p className="text-sm leading-relaxed text-text-secondary">
            A password reset email will go to the account's current address
            {details.profile.email !== null && (
              <>
                {" "}
                (<span className="font-medium text-text-primary">{details.profile.email}</span>)
              </>
            )}
            . If you just changed the email, the reset goes to the new address.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => {
                setConfirming(null);
                void runAction(
                  () => sendPasswordReset(id),
                  (value) => value.message,
                );
              }}
            >
              Send reset email
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={confirming === "verification"}
        onClose={() => setConfirming(null)}
        title="Resend verification email"
        size="sm"
      >
        <div className="space-y-4 px-6 py-5">
          <p className="text-sm leading-relaxed text-text-secondary">
            A verification email will go to{" "}
            {details.profile.email !== null ? (
              <span className="font-medium text-text-primary">{details.profile.email}</span>
            ) : (
              "the account's email address"
            )}
            .
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => {
                setConfirming(null);
                void runAction(
                  () => sendVerification(id),
                  (value) => value.message,
                );
              }}
            >
              Send verification email
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={confirming === "status"}
        onClose={() => setConfirming(null)}
        title="Change account status"
        size="sm"
      >
        <div className="space-y-4 px-6 py-5">
          <p className="text-sm leading-relaxed text-text-secondary">
            This account moves from{" "}
            <span className="font-medium text-text-primary">{statusLabel(details.status)}</span> to{" "}
            <span className="font-medium text-text-primary">{statusLabel(statusTarget)}</span>.
            {statusTarget === "DISABLED" || statusTarget === "BLOCKED"
              ? " The person will no longer be able to sign in."
              : " The person will be able to sign in again."}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => {
                setConfirming(null);
                void runAction(
                  () => setAccountStatus(id, statusTarget),
                  (value) => `Status changed to ${statusLabel(value.status)}.`,
                );
              }}
            >
              Change status
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={confirming === "messaging"}
        onClose={() => setConfirming(null)}
        title="Family messaging"
        size="sm"
      >
        <div className="space-y-4 px-6 py-5">
          <p className="text-sm leading-relaxed text-text-secondary">
            {messagingTarget
              ? `Families whose care team includes ${name} will be able to message them.`
              : `Families will no longer be able to start a message to ${name}.`}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => {
                setConfirming(null);
                void runAction(
                  () => setCanReceiveMessages(id, messagingTarget),
                  () =>
                    messagingTarget
                      ? "Family messages turned on."
                      : "Family messages turned off.",
                );
              }}
            >
              {messagingTarget ? "Allow family messages" : "Stop family messages"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function messagingLabel(value: boolean | null): string {
  return value === null ? "Not set" : value ? "Yes" : "No";
}

/** An undecided setting can go either way; a decided one only flips. */
function messagingTargets(value: boolean | null): boolean[] {
  return value === null ? [true, false] : [!value];
}

function HealthieRefusals({
  refusals,
  locked,
  disabled,
  onRetry,
}: {
  refusals: HealthieRefusal[];
  locked: boolean;
  disabled: boolean;
  onRetry: () => Promise<void>;
}) {
  const [retrying, setRetrying] = useState(false);

  async function retry() {
    setRetrying(true);
    await onRetry();
    setRetrying(false);
  }

  return (
    <section
      aria-label={HEALTHIE_REFUSAL_TITLE}
      className="enter flex flex-wrap items-start gap-3 rounded-2xl border border-warning-border bg-warning-bg px-4 py-3"
    >
      <TriangleAlert size={18} className="mt-0.5 shrink-0 text-warning" />
      <div className="min-w-0 flex-1">
        <h2 className="text-sm font-medium text-text-primary">{HEALTHIE_REFUSAL_TITLE}</h2>
        <ul className="mt-1.5 space-y-1.5">
          {refusals.map(({ field, reason, failedAt }) => (
            <li key={field} className="text-sm leading-relaxed text-text-secondary">
              <span className="font-medium text-text-primary">{HEALTHIE_FIELD_LABELS[field]}</span>
              {" · "}
              <span className="break-words">{reason ?? HEALTHIE_NO_REASON}</span>
              {" · "}
              <span className="text-xs text-text-tertiary">{formatWhen(failedAt)}</span>
            </li>
          ))}
        </ul>
      </div>
      <GuardedButton
        locked={locked}
        hint={STAFF_TARGET_HINT}
        variant="primary"
        size="sm"
        disabled={disabled || retrying}
        onClick={() => void retry()}
      >
        <BusyLabel busy={retrying} busyText="Sending…">
          Retry
        </BusyLabel>
      </GuardedButton>
    </section>
  );
}

function EmailActionsMenu({
  locked,
  hint,
  emailOnFile,
  onPick,
}: {
  locked: boolean;
  hint: string;
  emailOnFile: boolean;
  onPick: (action: "reset" | "verification") => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) {
      return;
    }
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);
  return (
    <div ref={triggerRef} className="relative" onClick={(e) => e.stopPropagation()}>
      <GuardedButton
        locked={locked}
        hint={hint}
        variant="secondary"
        size="md"
        onClick={() => setOpen((v) => !v)}
      >
        <Send size={15} />
        Send email
        <ChevronDown size={13} className="ml-0.5" />
      </GuardedButton>
      <DropdownPanel triggerRef={triggerRef} isOpen={open}>
        <div className="py-1">
          <button
            type="button"
            className="block w-full px-3.5 py-2 text-left text-sm text-text-primary transition-colors hover:bg-bg-hover"
            onClick={() => {
              setOpen(false);
              onPick("reset");
            }}
          >
            Password reset
            <span className="block text-xs text-text-tertiary">
              Reset link to the account's email
            </span>
          </button>
          <button
            type="button"
            disabled={!emailOnFile}
            className="block w-full px-3.5 py-2 text-left text-sm text-text-primary transition-colors hover:bg-bg-hover disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
              setOpen(false);
              onPick("verification");
            }}
          >
            Verification email
            <span className="block text-xs text-text-tertiary">
              Re-send the address verification link
            </span>
          </button>
        </div>
      </DropdownPanel>
    </div>
  );
}

function InviteList({
  details,
  busy,
  onResendCareTeam,
  onReinviteCoParent,
}: {
  details: AccountDetails;
  busy: boolean;
  onResendCareTeam: (inviteId: number) => void;
  onReinviteCoParent: (email: string, programKey: string | null) => void;
}) {
  const { coParent, careTeam } = details.invites;
  if (coParent.length === 0 && careTeam.length === 0) {
    return <p className="text-sm text-text-tertiary">No open invites.</p>;
  }
  return (
    <ul className="space-y-3">
      {careTeam.map((invite) => (
        <li key={`care-${invite.id}`} className="flex flex-wrap items-center gap-2 text-sm">
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-text-primary">{invite.inviteeEmail}</p>
            <p className="text-xs text-text-tertiary">
              Care-team invite · {invite.clientName ?? "—"} · sent {formatWhen(invite.createdAt)}
            </p>
          </div>
          {invite.linked ? (
            <Badge tone="success">Accepted</Badge>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => onResendCareTeam(invite.id)}
            >
              <Send size={14} />
              Resend
            </Button>
          )}
        </li>
      ))}
      {coParent.map((invite) => {
        const inviteEmail = invite.email;
        return (
          <li key={`co-${invite.id}`} className="flex flex-wrap items-center gap-2 text-sm">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-text-primary">{inviteEmail ?? "—"}</p>
              <p className="text-xs text-text-tertiary">
                Co-parent invite · sent {formatWhen(invite.createdAt)}
              </p>
            </div>
            {inviteEmail !== null && (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => onReinviteCoParent(inviteEmail, invite.programKey)}
              >
                <Send size={14} />
                Resend
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ChangeList({ changes }: { changes: AccountChange[] | null }) {
  if (changes === null) {
    return <Spinner size="sm" />;
  }
  if (changes.length === 0) {
    return <p className="text-sm text-text-tertiary">No staff changes recorded yet.</p>;
  }
  return (
    <ul className="space-y-3">
      {changes.map((change) => (
        <li key={change.id} className="flex items-start gap-2.5 text-sm">
          <History size={14} className="mt-0.5 shrink-0 text-text-tertiary" />
          <div className="min-w-0">
            <p className="text-text-primary">{changeSummary(change)}</p>
            <p className="text-xs text-text-tertiary">
              {formatWhen(change.changedAt)}
              {change.changedBy !== null && ` · by staff ${change.changedBy}`}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function changeSummary(change: AccountChange): string {
  switch (change.field) {
    case "action.unlock":
      return "Account unlocked";
    case "action.sendPasswordReset":
      return "Password reset email sent";
    case "action.sendVerification":
      return "Verification email sent";
    default:
      return `${CHANGE_FIELD_LABELS[change.field] ?? change.field} changed from ${
        change.oldValue ?? "empty"
      } to ${change.newValue ?? "empty"}`;
  }
}

/**
 * The Access editor: replaces the account's internal role set in one save. Client roles never
 * appear here; they belong to registration. The server additionally refuses self-changes and
 * non-admin actors, so this dialog is convenience, not the guard.
 */
function RolesModal({
  isOpen,
  accountId,
  current,
  onClose,
  onSaved,
}: {
  isOpen: boolean;
  accountId: string;
  current: string[];
  onClose: () => void;
  onSaved: (roles: string[]) => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelected(current.filter((role) => (INTERNAL_ROLES as readonly string[]).includes(role)));
      setError(null);
    }
  }, [isOpen, current]);

  function toggle(role: string) {
    setSelected((now) =>
      now.includes(role) ? now.filter((r) => r !== role) : [...now, role],
    );
  }

  async function save() {
    setSaving(true);
    setError(null);
    const result = await setAccountRoles(accountId, selected);
    setSaving(false);
    if (result.ok) {
      onSaved(result.value.roles);
    } else {
      setError(result.error.message);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit roles" size="sm">
      <div className="space-y-4 px-6 py-5">
        <p className="text-sm leading-relaxed text-text-secondary">
          The account holds exactly the roles selected here. Removing every role locks the person
          out of the console until a role is granted again.
        </p>
        <ul className="space-y-1.5">
          {INTERNAL_ROLES.map((role) => {
            const active = selected.includes(role);
            return (
              <li key={role}>
                <button
                  type="button"
                  onClick={() => toggle(role)}
                  aria-pressed={active}
                  className={cn(
                    "flex w-full items-center justify-between rounded-2xl border px-4 py-2.5 text-sm transition-colors",
                    active
                      ? "border-accent/40 bg-accent/10 text-text-primary"
                      : "border-border bg-bg-elevated text-text-secondary hover:bg-bg-hover",
                  )}
                >
                  {roleLabel(role)}
                  {active && <Check size={15} className="text-accent" />}
                </button>
              </li>
            );
          })}
        </ul>
        {error !== null && (
          <p role="alert" className="text-sm font-medium text-error">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" disabled={saving} onClick={() => void save()}>
            <BusyLabel busy={saving} busyText="Saving…">
              Save roles
            </BusyLabel>
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function EditAccountModal({
  isOpen,
  details,
  onClose,
  onSaved,
  onConcurrentConflict,
}: {
  isOpen: boolean;
  details: AccountDetails;
  onClose: () => void;
  onSaved: (message: string) => void;
  onConcurrentConflict: (message: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [legalFirstName, setLegalFirstName] = useState("");
  const [error, setError] = useState<ValidationError | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEmail(details.profile.email ?? "");
      setPhone(details.profile.phone ?? "");
      setFirstName(details.profile.firstName ?? "");
      setLastName(details.profile.lastName ?? "");
      setLegalFirstName(details.profile.legalFirstName ?? "");
      setError(null);
    }
  }, [isOpen, details]);

  async function save() {
    const profile = details.profile;
    const fields = [
      ["email", email, profile.email],
      ["phone", phone, profile.phone],
      ["firstName", firstName, profile.firstName],
      ["lastName", lastName, profile.lastName],
      ["legalFirstName", legalFirstName, profile.legalFirstName],
    ] as const;
    const blanked = fields.filter(
      ([, value, existing]) => value.trim() === "" && existing !== null,
    );
    if (blanked.length > 0) {
      setError(
        new ValidationError(
          "Fields can be changed but not cleared.",
          blanked.map(([field]) => ({ field, message: "cannot be cleared" })),
        ),
      );
      return;
    }
    const body: EditAccountBody = {};
    for (const [field, value, existing] of fields) {
      if (value !== (existing ?? "")) {
        body[field] = value;
      }
    }
    setSaving(true);
    setError(null);
    const result = await editAccount(String(profile.id), body);
    setSaving(false);
    if (result.ok) {
      onSaved(
        result.value.emailVerificationReset
          ? "Account updated. A verification email was sent to the new address."
          : "Account updated.",
      );
      return;
    }
    if (result.error.httpStatus === 409 && result.error.message.includes(CONCURRENT_EDIT)) {
      onConcurrentConflict(result.error.message);
      return;
    }
    if (result.error.httpStatus === 409 && result.error.fieldErrors.length === 0) {
      setError(new ValidationError(result.error.message, [
        { field: "email", message: result.error.message },
      ]));
      return;
    }
    setError(ValidationError.fromApiError(result.error));
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit account" size="md">
      <div className="space-y-4 px-6 py-5">
        <ErrorMessage message={error?.errorMessage ?? null} className="mb-0" />
        <Input
          label="Email"
          type="email"
          value={email}
          error={error?.getFieldError("email") ?? ""}
          onChange={(event) => {
            setEmail(event.target.value);
            setError((previous) => previous?.withoutField("email") ?? null);
          }}
        />
        <Input
          label="Phone"
          type="tel"
          value={phone}
          error={error?.getFieldError("phone") ?? ""}
          onChange={(event) => {
            setPhone(event.target.value);
            setError((previous) => previous?.withoutField("phone") ?? null);
          }}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="First name"
            value={firstName}
            error={error?.getFieldError("firstName") ?? ""}
            onChange={(event) => {
              setFirstName(event.target.value);
              setError((previous) => previous?.withoutField("firstName") ?? null);
            }}
          />
          <Input
            label="Last name"
            value={lastName}
            error={error?.getFieldError("lastName") ?? ""}
            onChange={(event) => {
              setLastName(event.target.value);
              setError((previous) => previous?.withoutField("lastName") ?? null);
            }}
          />
        </div>
        <Input
          label="Legal first name"
          value={legalFirstName}
          error={error?.getFieldError("legalFirstName") ?? ""}
          onChange={(event) => {
            setLegalFirstName(event.target.value);
            setError((previous) => previous?.withoutField("legalFirstName") ?? null);
          }}
        />
        <p className="text-xs leading-relaxed text-text-tertiary">
          The legal given name, if it differs from the name they go by. It combines with the last
          name to form the legal name on the medical record.
        </p>
        <p className="text-xs leading-relaxed text-text-tertiary">
          Changing the email resets its verified state; a verification email goes to the new
          address.
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={saving} onClick={() => void save()}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
